import React, { useRef, useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaBullseye, FaEye, FaHandsHelping, FaChartLine, FaLightbulb, FaStar, FaRocket, FaGlobe } from 'react-icons/fa';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  * {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background-color: #f8fafc;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const MissionContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 6rem 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -200px;
    right: -200px;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    filter: blur(80px);
    z-index: -1;
    animation: ${floatAnimation} 15s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -200px;
    left: -200px;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05));
    filter: blur(80px);
    z-index: -1;
    animation: ${floatAnimation} 18s ease-in-out infinite reverse;
  }
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
  filter: blur(20px);
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  animation: ${floatAnimation} ${props => props.duration || '20s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: -1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 6rem;
  position: relative;
`;

const Title = styled(motion.h2)`
  font-size: 4rem;
  font-weight: 900;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  animation: ${gradientAnimation} 5s ease infinite;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #4b5563;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
`;

const MissionVisionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 4rem;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MissionCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 10px 20px -5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.15), 0 15px 25px -5px rgba(0, 0, 0, 0.08);
    transform: translateY(-5px);
  }
`;

const CardImage = styled(motion.div)`
  height: 320px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.6));
    opacity: 0.7;
  }
`;

const CardContent = styled.div`
  padding: 2.5rem;
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CardIcon = styled(motion.div)`
  width: 70px;
  height: 70px;
  border-radius: 20px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  color: white;
  font-size: 1.8rem;
  flex-shrink: 0;
  box-shadow: 0 10px 20px -5px rgba(139, 92, 246, 0.4);
`;

const CardTitle = styled.h3`
  font-size: 2.2rem;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardText = styled.p`
  font-size: 1.15rem;
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const ValuesSection = styled(motion.div)`
  margin-top: 8rem;
  text-align: center;
  position: relative;
`;

const ValuesTitle = styled.h3`
  font-size: 2.8rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 4rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${gradientAnimation} 5s ease infinite;
  display: inline-block;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  margin-top: 2rem;
`;

const ValueCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.15);

    &::before {
      opacity: 1;
    }
  }
`;

const ValueIcon = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: #8b5cf6;
  font-size: 2.5rem;
  position: relative;
  transition: all 0.3s ease;

  ${ValueCard}:hover & {
    transform: rotate(5deg) scale(1.1);
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.15));
  }
`;

const ValueTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  font-size: 1.05rem;
  color: #4b5563;
  line-height: 1.7;
`;

const Sparkle = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.8);
  opacity: 0;
  z-index: 2;
`;

const Mission = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const values = [
    {
      icon: <FaHandsHelping />,
      title: "Collaboration",
      description: "We believe in the power of teamwork and shared success, working together to achieve extraordinary results."
    },
    {
      icon: <FaChartLine />,
      title: "Innovation",
      description: "Continuous improvement and forward-thinking approaches drive our competitive advantage and growth."
    },
    {
      icon: <FaLightbulb />,
      title: "Creativity",
      description: "We encourage bold ideas, unconventional thinking, and creative solutions to complex challenges."
    },
    {
      icon: <FaGlobe />,
      title: "Impact",
      description: "We strive to make a positive difference in the world through our work and initiatives."
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <>
      <GlobalStyle />
      <MissionContainer ref={ref}>
        <FloatingOrb top="10%" left="5%" duration="25s" delay="0s" />
        <FloatingOrb top="60%" right="8%" duration="30s" delay="2s" />
        <FloatingOrb bottom="15%" left="15%" duration="22s" delay="1s" />
        
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Our Purpose & Direction
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            Guiding principles that shape our decisions, actions, and future aspirations
          </Subtitle>
        </Header>

        <MissionVisionGrid>
          <MissionCard
            variants={cardVariants}
            initial="hidden"
            animate={controls}
            whileHover={{ y: -8 }}
            onHoverStart={() => setHoveredCard('mission')}
            onHoverEnd={() => setHoveredCard(null)}
          >
            <CardImage>
              <motion.img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                alt="Our Mission"
                animate={{ scale: hoveredCard === 'mission' ? 1.05 : 1 }}
                transition={{ duration: 10 }}
              />
            </CardImage>
            <CardContent>
              <CardHeader>
                <CardIcon
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 5, repeat: Infinity }}
                >
                  <FaBullseye />
                </CardIcon>
                <CardTitle>Our Mission</CardTitle>
              </CardHeader>
              <CardText>
                To empower businesses and individuals through innovative solutions that drive sustainable growth, 
                foster meaningful connections, and create lasting value for all stakeholders. We are committed to 
                excellence in everything we do, pushing boundaries to deliver exceptional results that make a difference.
              </CardText>
            </CardContent>
          </MissionCard>

          <MissionCard
            variants={cardVariants}
            initial="hidden"
            animate={controls}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -8 }}
            onHoverStart={() => setHoveredCard('vision')}
            onHoverEnd={() => setHoveredCard(null)}
          >
            <CardImage>
              <motion.img 
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                alt="Our Vision"
                animate={{ scale: hoveredCard === 'vision' ? 1.05 : 1 }}
                transition={{ duration: 10 }}
              />
            </CardImage>
            <CardContent>
              <CardHeader>
                <CardIcon
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                >
                  <FaEye />
                </CardIcon>
                <CardTitle>Our Vision</CardTitle>
              </CardHeader>
              <CardText>
                To be the global benchmark for innovation and excellence in our industry, transforming the way 
                businesses operate and people live. We envision a future where our solutions create opportunities, 
                bridge divides, and inspire positive change on a global scale, building a better tomorrow for all.
              </CardText>
            </CardContent>
          </MissionCard>
        </MissionVisionGrid>

        <ValuesSection
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          <ValuesTitle>Our Core Values</ValuesTitle>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                variants={cardVariants}
                whileHover={{ y: -12 }}
              >
                <ValueIcon>
                  {value.icon}
                </ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesSection>
      </MissionContainer>
    </>
  );
};

export default Mission;